// Derives the per-sleeve live footer from data App already fetched. Fetches nothing itself.
import { useMemo } from 'react'
import type { PortfolioSnapshot, Position, RegimeState, StrategyConfig } from '../../lib/supabase'
import { NAV_CAP_PCT, SLEEVE_SHARE_CAP, type SleeveKey } from './strategyContent'

export interface LivePosition { ticker: string; pctNav: number }

export interface SleeveLive {
  targetPct: number | null
  scalar: number | null
  scaledTargetPct: number | null
  capPct: number | null          // D080 effective per-position cap, % of NAV
  positions: LivePosition[]      // sorted desc by pctNav
  deployedPct: number
}

export interface StrategyLive {
  available: boolean             // false = demo/fallback data or no snapshot: show no numbers
  reason: string | null
  asOf: string | null            // snapshot date (YYYY-MM-DD)
  regimeDate: string | null
  stale: boolean
  sleeves: Record<SleeveKey, SleeveLive>
}

const KEYS: SleeveKey[] = ['etf', 'stock', 'ipo', 'commod', 'crypto']

export function effectiveCap(key: SleeveKey, scaledTargetPct: number | null): number | null {
  if (scaledTargetPct == null) return null
  return Math.min(NAV_CAP_PCT[key], SLEEVE_SHARE_CAP * scaledTargetPct)
}

function scalarFor(key: SleeveKey, rs: RegimeState | null): number | null {
  if (!rs) return null
  const v = key === 'etf' || key === 'stock' ? rs.regime_scalar
    : key === 'ipo' ? rs.ipo_scalar : key === 'commod' ? rs.commod_scalar : rs.crypto_scalar
  return v ?? null
}

export function useStrategyLive(
  positions: Position[],
  snapshot: PortfolioSnapshot | null,
  regimeState: RegimeState | null,
  strategyConfig: StrategyConfig | null,
  fallbackTargets: Record<SleeveKey, number>,
  isDemoMode: boolean,
  today: string,
): StrategyLive {
  return useMemo(() => {
    const nav = snapshot?.net_worth ?? 0
    // App substitutes DEMO_SNAPSHOT (account_id 'demo') when the snapshot fetch fails: never treat it as live.
    const demoSnap = snapshot?.account_id === 'demo'
    const missingPositions = positions.length === 0 && (snapshot?.position_count ?? 0) > 0
    const reason = isDemoMode || demoSnap ? 'Live data unavailable: showing no position numbers.'
      : !snapshot ? 'Live data unavailable: no portfolio snapshot.'
      : nav <= 0 ? 'Live data unavailable: snapshot has no net worth.'
      : missingPositions ? `Live data unavailable: the snapshot reports ${snapshot.position_count} positions but none loaded.` : null
    const available = reason === null
    const sleeves = {} as Record<SleeveKey, SleeveLive>
    for (const k of KEYS) {
      const cfgT = strategyConfig?.sleeve_target_pct?.[k]
      const targetPct = cfgT != null ? cfgT * 100 : fallbackTargets[k]
      // Demo or missing live data: show no regime-derived numbers either (DEMO_REGIME_STATE is fabricated).
      const scalar = available ? scalarFor(k, regimeState) : null
      const scaledTargetPct = scalar != null ? targetPct * scalar : null
      const pos = available
        ? positions.filter(p => p.sleeve === k && p.quantity > 0).map(p => ({ ticker: p.ticker, pctNav: (p.market_value / nav) * 100 }))
            .sort((a, b) => b.pctNav - a.pctNav)
        : []
      sleeves[k] = {
        targetPct, scalar, scaledTargetPct, capPct: effectiveCap(k, scaledTargetPct),
        positions: pos, deployedPct: pos.reduce((a, p) => a + p.pctNav, 0),
      }
    }
    const asOf = snapshot?.snapshot_date ?? null
    return {
      available, reason, asOf, regimeDate: regimeState?.computed_date ?? null,
      stale: (!!asOf && asOf < today) || (!!regimeState?.computed_date && regimeState.computed_date < today), sleeves,
    }
  }, [positions, snapshot, regimeState, strategyConfig, fallbackTargets, isDemoMode, today])
}
